import { Fragment, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  Plus,
  Loader2,
  Circle,
  CheckCircle2,
  XCircle,
  MoreVertical,
  StickyNote,
  Trash2,
  X,
} from 'lucide-react';
import { cn, isValidUUID } from '@/lib/utils';
import { Button, Input } from '@/components/ui';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/features/auth';
import { useChecklistItems } from '../hooks/useChecklistItems';
import { useTranslation } from '@parentpath/i18n';
import type { UserChecklist, ItemStatus, UserChecklistItem } from '../types';
import { RecommendationSlot, useRandomInsertionIndex } from '@/features/recommendations';

// Inline ChecklistItem component
function ChecklistItemRow({
  item,
  onToggle,
  onMarkNotNeeded,
  onUpdateNotes,
  onDelete,
  isCustomItem,
  currentUserId,
}: {
  item: UserChecklistItem;
  onToggle: () => void;
  onMarkNotNeeded: () => void;
  onUpdateNotes: (notes: string) => void;
  onDelete?: () => void;
  isCustomItem?: boolean;
  currentUserId?: string;
}) {
  const { t } = useTranslation('checklists');
  const [showMenu, setShowMenu] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const [notes, setNotes] = useState(item.notes || '');

  const handleSaveNotes = () => {
    onUpdateNotes(notes);
    setShowNotes(false);
  };

  const handleDeleteNotes = () => {
    onUpdateNotes('');
    setNotes('');
    setShowNotes(false);
  };

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));

    if (hours < 1) return t('time.justNow');
    if (hours < 24) return t('time.hoursAgo', { hours });
    const days = Math.floor(hours / 24);
    if (days === 1) return t('time.yesterday');
    return t('time.daysAgo', { days });
  };

  return (
    <div className="relative">
      <div
        className={cn(
          'checklist-item',
          item.status === 'completed' && 'completed',
          item.status === 'not_needed' && 'not-needed'
        )}
      >
        {/* Checkbox Icon */}
        <button onClick={onToggle} className="flex-shrink-0">
          {item.status === 'completed' ? (
            <CheckCircle2 className="w-6 h-6 text-white" />
          ) : item.status === 'not_needed' ? (
            <XCircle className="w-6 h-6 text-white/70" />
          ) : (
            <Circle className="w-6 h-6 text-white/70" />
          )}
        </button>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <span className="text-white">{item.title}</span>

          {/* Notes Preview */}
          {item.notes && (
            <div className="flex flex-col pt-1">
              <span className="text-xs italic text-white/80">"{item.notes}"</span>
              {item.notes_updated_at && (
                <span className="text-white/50 text-xs">
                  {t('items.noteBy', { author: item.notes_updated_by === currentUserId ? t('items.noteByMe') : (item.notes_updated_by_name || t('items.noteByPartner')), time: formatTime(item.notes_updated_at) })}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Menu Button */}
        <button
          onClick={() => setShowMenu(!showMenu)}
          className="text-white/80 p-1"
        >
          <MoreVertical className="w-5 h-5" />
        </button>

      </div>

      {/* Context Menu Bottom Sheet */}
      {showMenu && createPortal(
        <>
          <div className="bottom-sheet-backdrop" onClick={() => setShowMenu(false)} />
          <div className="bottom-sheet-container">
            <div className="bottom-sheet-modal">
              {/* Header */}
              <div className="bottom-sheet-header">
                <h3 className="font-semibold text-white flex-1">{item.title}</h3>
                <button
                  onClick={() => setShowMenu(false)}
                  className="w-8 h-8 rounded-[5px] bg-white/20 flex items-center justify-center flex-shrink-0"
                >
                  <X className="w-4 h-4 text-white" />
                </button>
              </div>

              {/* Actions */}
              <div className="bottom-sheet-footer !flex-col !gap-2">
                <button
                  className="w-full flex items-center gap-3 p-3 rounded-[10px] bg-white/10 text-white hover:bg-white/20 transition-colors"
                  onClick={() => {
                    setShowNotes(true);
                    setShowMenu(false);
                  }}
                >
                  <StickyNote className="w-5 h-5" />
                  <span className="font-medium">{t('items.note')}</span>
                </button>
                {item.status !== 'not_needed' ? (
                  <button
                    className="w-full flex items-center gap-3 p-3 rounded-[10px] bg-white/10 text-white hover:bg-white/20 transition-colors"
                    onClick={() => {
                      onMarkNotNeeded();
                      setShowMenu(false);
                    }}
                  >
                    <XCircle className="w-5 h-5" />
                    <span className="font-medium">{t('items.notNeeded')}</span>
                  </button>
                ) : (
                  <button
                    className="w-full flex items-center gap-3 p-3 rounded-[10px] bg-white/10 text-white hover:bg-white/20 transition-colors"
                    onClick={() => {
                      onToggle();
                      setShowMenu(false);
                    }}
                  >
                    <Circle className="w-5 h-5" />
                    <span className="font-medium">{t('items.markOpen')}</span>
                  </button>
                )}
                {isCustomItem && onDelete && (
                  <button
                    className="w-full flex items-center gap-3 p-3 rounded-[10px] bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors"
                    onClick={() => {
                      onDelete();
                      setShowMenu(false);
                    }}
                  >
                    <Trash2 className="w-5 h-5" />
                    <span className="font-medium">{t('items.delete')}</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </>,
        document.body
      )}

      {/* Notes Bottom Sheet */}
      {showNotes && createPortal(
        <>
          <div className="bottom-sheet-backdrop" onClick={() => setShowNotes(false)} />
          <div className="bottom-sheet-container">
            <div className="bottom-sheet-modal">
              <div className="bottom-sheet-header">
                <h3 className="font-semibold text-white flex-1">{t('items.noteFor', { title: item.title })}</h3>
                <button
                  onClick={() => setShowNotes(false)}
                  className="w-8 h-8 rounded-[5px] bg-white/20 flex items-center justify-center flex-shrink-0"
                >
                  <X className="w-4 h-4 text-white" />
                </button>
              </div>
              <div className="bottom-sheet-content">
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder={t('items.notePlaceholder')}
                  className="w-full p-3 bg-white/10 border border-white/25 rounded-[10px] resize-none h-24 text-sm text-white placeholder:text-white/50"
                  autoFocus
                />
              </div>
              <div className="bottom-sheet-footer">
                <button
                  onClick={handleDeleteNotes}
                  disabled={!item.notes}
                  className="flex-1 py-2.5 rounded-xl bg-red-500/20 border border-red-500/30 text-red-400 font-medium hover:bg-red-500/30 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {t('items.delete')}
                </button>
                <Button
                  onClick={handleSaveNotes}
                  className="flex-1 bg-pp-orange hover:bg-pp-orange/90"
                >
                  {t('items.save')}
                </Button>
              </div>
            </div>
          </div>
        </>,
        document.body
      )}
    </div>
  );
}

export function ChecklistDetailPage() {
  const { id: rawId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation('checklists');

  const FILTER_OPTIONS: { value: 'all' | ItemStatus; label: string }[] = [
    { value: 'all', label: t('filters.all') },
    { value: 'open', label: t('filters.open') },
    { value: 'completed', label: t('filters.completed') },
    { value: 'not_needed', label: t('filters.notNeeded') },
  ];

  const [checklist, setChecklist] = useState<UserChecklist | null>(null);
  const [isLoadingChecklist, setIsLoadingChecklist] = useState(true);
  const [showAddItem, setShowAddItem] = useState(false);
  const [newItemTitle, setNewItemTitle] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Validate the ID from URL params to prevent 400 errors
  const id = isValidUUID(rawId) ? rawId : null;

  const {
    items,
    isLoading: isLoadingItems,
    progress,
    filters,
    setFilters,
    toggleItem,
    markNotNeeded,
    updateNotes,
    addItem,
    deleteItem,
  } = useChecklistItems({ checklistId: id || '' });

  // Stable random index (recomputed when item count changes) for injecting
  // the product recommendation between checklist items
  const recommendationIndex = useRandomInsertionIndex('checklist_detail', items.length);

  // Fetch checklist details
  useEffect(() => {
    async function fetchChecklist() {
      if (!id) {
        setIsLoadingChecklist(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('user_checklists')
          .select('*')
          .eq('id', id)
          .single();

        if (error) throw error;
        setChecklist(data);
      } catch (err) {
        console.error('Error fetching checklist:', err);
      } finally {
        setIsLoadingChecklist(false);
      }
    }

    fetchChecklist();
  }, [id]);

  const handleAddItem = async () => {
    if (!newItemTitle.trim()) return;
    setIsAdding(true);
    await addItem(newItemTitle.trim());
    setNewItemTitle('');
    setShowAddItem(false);
    setIsAdding(false);
  };

  const handleDeleteChecklist = async () => {
    if (!id) return;

    try {
      const { error } = await supabase
        .from('user_checklists')
        .delete()
        .eq('id', id);

      if (error) throw error;
      navigate('/tools/checklists');
    } catch (err) {
      console.error('Error deleting checklist:', err);
    }
  };

  if (isLoadingChecklist || isLoadingItems) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-white" />
      </div>
    );
  }

  if (!checklist) {
    return (
      <div className="text-center py-12">
        <p className="text-white/70">{t('detail.notFound')}</p>
        <Link to="/tools/checklists" className="text-pp-orange mt-2 inline-block">
          {t('detail.backToChecklists')}
        </Link>
      </div>
    );
  }

  return (
    <>
      {/* Header */}
      <h1 className="text-white text-xl font-semibold mb-2">{checklist.title}</h1>
      <p className="text-white/70 text-sm mb-3">
        {t('detail.completed', { completed: progress.completed, total: progress.total })}
      </p>

      {/* Progress Bar */}
      <div className="checklist-progress-bar mb-6">
        <div
          className="checklist-progress-fill"
          style={{ width: `${progress.percentage}%` }}
        ></div>
      </div>

      {/* Filter Tabs with Delete Button */}
      <div className="flex items-center gap-3 mb-4 mt-8">
        <div className="filter-tabs flex-1 min-w-0">
          {FILTER_OPTIONS.map((option) => (
            <button
              key={option.value}
              onClick={() => setFilters({ status: option.value })}
              className={cn(
                'filter-tab',
                filters.status === option.value && 'active'
              )}
            >
              {option.label}
              {option.value !== 'all' && (
                <span>
                  {option.value === 'open'
                    ? progress.open
                    : option.value === 'completed'
                    ? progress.completed
                    : progress.notNeeded || 0}
                </span>
              )}
              {option.value === 'all' && <span>{progress.total}</span>}
            </button>
          ))}
        </div>
        <button
          onClick={() => setShowDeleteConfirm(true)}
          className="flex-shrink-0 w-8 h-8 rounded-[5px] bg-pp-orange flex items-center justify-center hover:bg-pp-orange/90 transition-colors"
        >
          <Trash2 className="w-4 h-4 text-white" />
        </button>
      </div>

      {/* Checklist Items */}
      <section className="space-y-3">
        {items.length === 0 ? (
          <div className="glass-effect rounded-2xl p-8 text-center">
            <p className="text-white/70">
              {filters.status === 'all'
                ? t('items.noItems')
                : t('items.noFilteredItems', { filter: FILTER_OPTIONS.find(o => o.value === filters.status)?.label.toLowerCase() })}
            </p>
          </div>
        ) : (
          <>
            {items.map((item, idx) => (
              <Fragment key={item.id}>
                {idx === recommendationIndex && (
                  <RecommendationSlot placement="checklist_detail" position="random" />
                )}
                <ChecklistItemRow
                  item={item}
                  onToggle={() => toggleItem(item.id, item.status)}
                  onMarkNotNeeded={() => markNotNeeded(item.id)}
                  onUpdateNotes={(notes) => updateNotes(item.id, notes)}
                  onDelete={() => deleteItem(item.id)}
                  isCustomItem={!item.template_item_id}
                  currentUserId={user?.id}
                />
              </Fragment>
            ))}
            {recommendationIndex === items.length && (
              <RecommendationSlot placement="checklist_detail" position="random" />
            )}
            {/* End position */}
            <RecommendationSlot placement="checklist_detail" position="end" />
          </>
        )}
      </section>

      {/* Add Button */}
      <div className="mt-6">
        {!showAddItem ? (
          <button className="create-btn" onClick={() => setShowAddItem(true)}>
            <Plus className="w-5 h-5" />
            {t('items.add')}
          </button>
        ) : (
          <div className="glass-effect rounded-2xl p-4 space-y-3">
            <Input
              placeholder={t('items.addPlaceholder')}
              value={newItemTitle}
              onChange={(e) => setNewItemTitle(e.target.value)}
              autoFocus
              onKeyDown={(e) => e.key === 'Enter' && handleAddItem()}
              className="bg-white/10 border-white/25 text-white placeholder:text-white/50"
            />
            <div className="flex gap-2">
              <Button
                variant="secondary"
                className="flex-1 bg-white/20 text-white border-white/25"
                onClick={() => {
                  setShowAddItem(false);
                  setNewItemTitle('');
                }}
              >
                {t('items.cancel')}
              </Button>
              <Button
                className="flex-1 bg-pp-orange hover:bg-pp-orange/90"
                onClick={handleAddItem}
                disabled={!newItemTitle.trim() || isAdding}
              >
                {isAdding ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  t('items.add')
                )}
              </Button>
            </div>
          </div>
        )}

      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && createPortal(
        <>
          <div className="bottom-sheet-backdrop" onClick={() => setShowDeleteConfirm(false)} />
          <div className="bottom-sheet-container">
            <div className="bottom-sheet-modal">
              <div className="bottom-sheet-content">
                <h3 className="text-lg font-semibold text-white mb-2">
                  {checklist.is_custom ? t('deleteConfirm.deleteTitle') : t('deleteConfirm.removeTitle')}
                </h3>
                <p className="text-white/70">
                  {checklist.is_custom
                    ? t('deleteConfirm.deleteDescription')
                    : t('deleteConfirm.removeDescription')}
                </p>
              </div>
              <div className="bottom-sheet-footer">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="flex-1 py-2.5 rounded-xl bg-white/20 border border-white/25 text-white font-medium hover:bg-white/30"
                >
                  {t('deleteConfirm.cancel')}
                </button>
                <button
                  onClick={handleDeleteChecklist}
                  className="flex-1 py-2.5 rounded-xl bg-red-500 text-white font-medium"
                >
                  {checklist.is_custom ? t('deleteConfirm.delete') : t('deleteConfirm.remove')}
                </button>
              </div>
            </div>
          </div>
        </>,
        document.body
      )}
    </>
  );
}
